import * as Yup from 'yup';
import {
  AVAILABLE_TIMEZONES,
  Select,
  SelectOption,
  SubAccountBalanceView,
  SubmitUserSettingsRequest,
  Timezone,
  Tooltip,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { FieldData } from '@/components/Form/types';
import { Title } from '@/components/UI';

export type UserSettingsFormValues = Omit<SubmitUserSettingsRequest, 'EmailAddress'>;

export function useUserSettingsFields(): FieldData<UserSettingsFormValues>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'User.Settings' });

  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));

  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  const languageOptions: SelectOption<{ label: string; value: string }>[] = [
    { label: t('Language.Options.en'), value: 'en' },
    { label: t('Language.Options.fr'), value: 'fr' },
  ];

  const subAccountBalanceViewOptions: SelectOption<{
    label: string;
    value: SubAccountBalanceView;
  }>[] = [
    {
      label: t('SubAccountBalanceDisplay.Options.none'),
      value: 'do-not-show-balances',
    },
    {
      label: t('SubAccountBalanceDisplay.Options.rollUp'),
      value: 'roll-up-balance',
    },
    {
      label: t('SubAccountBalanceDisplay.Options.available'),
      value: 'available-balance',
    },
  ];

  return [
    {
      label: () => t('timeZone'),
      fieldName: 'Timezone',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: UserSettingsFormValues) => values.Timezone,
      tooltip: () => (
        <Tooltip
          variant="outlined"
          icon="info"
          buttonWidth="20px"
          iconWidth="12px"
          type="hover"
          information={t('tooltipMessage')}
          position="right"
        />
      ),
      renderField: ({ getFormikData }) => {
        const formikData = getFormikData<Timezone>({});

        return <Select options={timeZoneOptions} version="2" placeholder="" {...formikData} filterable sort={false} />;
      },
      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('Language.label'),
      fieldName: 'Language',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: UserSettingsFormValues) => values.Language,
      tooltip: () => null,
      renderField: ({ getFormikData }) => {
        const formikData = getFormikData<string>({});

        return <Select options={languageOptions} version="2" placeholder="" {...formikData} sort={false} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('SubAccountBalanceDisplay.label'),
      fieldName: 'SubAccountBalanceDisplay',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: UserSettingsFormValues) => values.SubAccountBalanceDisplay,
      tooltip: () => (
        <Tooltip
          variant="outlined"
          icon="info"
          buttonWidth="20px"
          iconWidth="12px"
          type="hover"
          information={
            <div>
              <Title level="6">{t('SubAccountBalanceDisplay.tooltipTitle')}</Title>
              {t('SubAccountBalanceDisplay.tooltip')}
            </div>
          }
          position="right"
        />
      ),
      renderField: ({ getFormikData }) => {
        const formikData = getFormikData<SubAccountBalanceView>({});

        return (
          <Select options={subAccountBalanceViewOptions} version="2" placeholder="" {...formikData} sort={false} />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];
}
